import { avgsForm } from '../templates'
import { captureWriteIdentity, type WriteIdentity } from '@/core/api/http'
import { extractAvgs, type AvgsForm } from '../lib/extractAvgs'
import { ok, technical, type Result, type FieldId } from '@/core/lib/result'
import {
  validatePresence,
  presenceRulesFromTemplate,
  findMalformedDates,
  findMalformedNumbers,
  AVGS_DATE_PATHS,
  AVGS_NUMBER_PATHS,
} from '@/core/lib/validate'
import type { FieldError, FieldErrors } from '@/core/lib/fieldError'
import {
  getDocData,
  getDocBucketAddress,
  patchDocData,
  relocateDocObject,
  upsertDoc,
} from '@/core/data/dokumente'
import {
  uploadFile,
  downloadFile,
  removeFile,
  docPath,
  versionedFile,
} from '@/core/data/storage'
import {
  promoteAvgs,
  listAvgsCandidates,
  setAufgabe,
  rewindJourney,
  getJourneyPosition,
  type AvgsCandidate,
} from '@/core/data/coachings'
import { AUFGABE, type AufgabeKey } from '@/core/data/aufgabe'
import { planAvgsPromotion } from './avgsPromote'
import { matchTeilnehmende } from '../lib/matchTeilnehmende'
import { assemblePages, splitAt, splitWithPlan, copyPages, assembledFromBytes } from '@/core/lib/pdfSplit'
import { classifyPages, type SplitPlan } from '../lib/classifyPages'
import { TB_EMPTY, discardBlankTb, invalidateFilledTb } from './tb'
import { getTbEinreichung, setTbEinreichung } from '../data/coaching'
import { isJobcenter } from '../lib/berichtTemplate'

async function advanceBestEffort(
  coachingId: string,
  aufgabe: AufgabeKey,
): Promise<void> {
  try {
    await setAufgabe(coachingId, aufgabe)
  } catch (e) {
    console.error(`Aufgabe "${aufgabe}" konnte nicht gesetzt werden:`, e)
  }
}

async function settleBestEffort(coachingId: string, aufgabe: AufgabeKey): Promise<void> {
  try {
    const moved = await setAufgabe(coachingId, aufgabe)
    if (!moved.advanced) await rewindJourney(coachingId, aufgabe, 'tbPruefen')
  } catch (e) {
    console.error(`Aufgabe "${aufgabe}" konnte nicht gesetzt werden:`, e)
  }
}

async function cleanupAll(paths: readonly string[]): Promise<void> {
  for (const p of paths) await removeFile(p).catch(() => {})
}

async function uploadAll(
  objects: readonly { name: string; blob: Blob }[],
): Promise<string[] | null> {
  const landed: string[] = []
  try {
    for (const o of objects) {
      landed.push(await uploadFile(o.blob, o.name))
    }
  } catch (e) {
    console.error('AVGS/Trägerbestätigung speichern fehlgeschlagen:', e)
    await cleanupAll(landed)
    return null
  }
  return landed
}

async function retirePrevious(prev: string | null, next?: string): Promise<void> {
  if (prev && prev !== next) await cleanupAll([prev])
}

const avgsPresenceRules = presenceRulesFromTemplate(avgsForm, {
  optional: ['Maßnahme.Zeitlicher Umfang (max UE)', 'Maßnahme.Beschränkungen'],
  oneOf: [
    ['Maßnahme.Maßnahmeinhalt (Gruppenmaßnahme)', 'Maßnahme.Maßnahmeinhalt (Einzelmaßnahme)'],
  ],
})

export const avgsSelectRequiredFields: ReadonlySet<FieldId> = new Set([
  'Teilnehmer.Anrede',
  'Maßnahme.Zeitlicher Umfang (Kategorie)',
])


export type AvgsCheck = { errors: FieldErrors }

export function checkAvgs(values: Record<string, unknown>): AvgsCheck {
  const { missing, unsatisfiedGroups } = validatePresence(values, avgsPresenceRules)
  const errors = new Map<FieldId, FieldError>()
  for (const path of missing) errors.set(path, { kind: 'missing' })
  for (const path of findMalformedDates(values, AVGS_DATE_PATHS)) {
    if (!errors.has(path)) errors.set(path, { kind: 'malformed', expected: 'date' })
  }
  for (const path of findMalformedNumbers(values, AVGS_NUMBER_PATHS)) {
    if (!errors.has(path)) errors.set(path, { kind: 'malformed', expected: 'number' })
  }
  for (const group of unsatisfiedGroups) {
    for (const path of group) errors.set(path, { kind: 'oneOf' })
  }
  return { errors }
}

const AVGS = 'avgs'

async function dropStaleOriginal(
  prevDaten: Record<string, unknown> | null,
  currentOriginal?: string,
): Promise<void> {
  const original = prevDaten?.combinedOriginal
  if (typeof original === 'string' && original !== currentOriginal) {
    await cleanupAll([original])
  }
}

async function writeSplitPair(fields: {
  coachingId: string
  avgsDaten: Record<string, unknown>
  avgsPath: string
  avgsSize: number
  tbPath: string
  tbSize: number
  prevAvgsPath: string | null
  restoreDaten: Record<string, unknown> | null
  cleanupPaths: readonly string[]
}): Promise<{ ok: true } | { ok: false; error: unknown }> {
  const { coachingId } = fields
  try {
    await upsertDoc(coachingId, AVGS, {
      daten: fields.avgsDaten,
      bucketAddress: fields.avgsPath,
      fileType: 'application/pdf',
      fileSize: fields.avgsSize,
    })
    await upsertDoc(coachingId, TB_EMPTY, {
      bucketAddress: fields.tbPath,
      fileType: 'application/pdf',
      fileSize: fields.tbSize,
    })
  } catch (e) {
    console.error('AVGS/Trägerbestätigung speichern fehlgeschlagen:', e)
    await upsertDoc(coachingId, AVGS, {
      bucketAddress: fields.prevAvgsPath ?? '',
      daten: fields.restoreDaten ?? {},
    }).catch((restoreErr) => {
      console.error('AVGS-Dokument konnte nicht zurückgesetzt werden:', restoreErr)
    })
    await cleanupAll(fields.cleanupPaths)
    return { ok: false, error: e }
  }
  return { ok: true }
}

export async function getAvgsPath(coachingId: string): Promise<string | null> {
  return getDocBucketAddress(coachingId, AVGS)
}

export type CombinedUploadStage = 'splitting' | 'classifying' | 'storing' | 'extracting'

function blobFromBytes(bytes: Uint8Array): Blob {
  return new Blob([new Uint8Array(bytes)], { type: 'application/pdf' })
}

async function classifyWithFallback(
  uploadPath: string | Blob,
  pageCount: number,
): Promise<{ plan: SplitPlan; warnings: string[] }> {
  const warnings: string[] = []
  const fallback: SplitPlan = {
    avgsPageIndicesInOrder: [0],
    tbPageIndicesInOrder: Array.from({ length: pageCount - 1 }, (_, i) => i + 1),
    ignoredPageIndices: [],
  }
  const classified = await classifyPages(uploadPath)
  if (!classified.ok) {
    console.error('Seitenklassifizierung fehlgeschlagen:', classified)
    warnings.push(
      'Automatische Seitenzuordnung nicht möglich – Standardreihenfolge (Seite 1 = AVGS) angenommen.',
    )
    return { plan: fallback, warnings }
  }
  const plan = classified.data
  if (plan.ignoredPageIndices.length > 0) {
    const n = plan.ignoredPageIndices.length
    warnings.push(
      n === 1
        ? '1 Seite konnte nicht zugeordnet werden und wurde ignoriert.'
        : `${n} Seiten konnten nicht zugeordnet werden und wurden ignoriert.`,
    )
  }
  return { plan, warnings }
}

type CombinedPages = { originalPath: string; avgs: Blob; tb: Blob | null; warnings: string[] }

async function prepareCombinedPages(
  drop: { bytes: Uint8Array; pageCount: number },
  onProgress?: (stage: CombinedUploadStage) => void,
): Promise<Result<CombinedPages>> {
  onProgress?.('classifying')
  let originalPath: string
  try {
    originalPath = await uploadFile(blobFromBytes(drop.bytes), versionedFile('upload', 'pdf'))
  } catch (error) {
    console.error('AVGS/Trägerbestätigung speichern fehlgeschlagen:', error)
    return technical('AVGS/Trägerbestätigung konnte nicht gespeichert werden.')
  }
  const { plan, warnings } = await classifyWithFallback(blobFromBytes(drop.bytes), drop.pageCount)
  const assembled = { bytes: drop.bytes, pageCount: drop.pageCount }
  if (plan.tbPageIndicesInOrder.length === 0) {
    warnings.push(NO_TB_WARNING)
    const avgs = await copyPages(assembled, plan.avgsPageIndicesInOrder)
    if (avgs.ok) return ok({ originalPath, avgs: avgs.data, tb: null, warnings })
    await cleanupAll([originalPath])
    return avgs
  }
  onProgress?.('storing')
  const split = await splitWithPlan(assembled, plan.avgsPageIndicesInOrder, plan.tbPageIndicesInOrder)
  if (split.ok) return ok({ originalPath, avgs: split.data.avgs, tb: split.data.tb, warnings })
  await cleanupAll([originalPath])
  return split
}

async function extractStoredAvgs(
  coachingId: string,
  path: string,
  originalPath?: string,
): Promise<Result<AvgsForm>> {
  const extracted = await extractAvgs(path)
  if (!extracted.ok) return extracted
  try {
    await upsertDoc(coachingId, AVGS, {
      daten: { ...(originalPath === undefined ? {} : { combinedOriginal: originalPath }), extraction: extracted.data },
    })
    return extracted
  } catch (error) {
    console.error('AVGS speichern fehlgeschlagen:', error)
    return technical('AVGS konnte nicht gespeichert werden.')
  }
}

/** The missing blank is only news on the digital submission path. */
export const NO_TB_WARNING = 'Keine Trägerbestätigung erkannt – nur der AVGS wurde gespeichert.'

export async function applyCombinedUpload(
  coachingId: string,
  files: readonly File[],
  onProgress?: (stage: CombinedUploadStage) => void,
): Promise<Result<{ form: AvgsForm; tbStored: boolean; warnings?: string[] }>> {
  onProgress?.('splitting')
  const drop = await assemblePages(files)
  if (!drop.ok) return drop
  if (drop.data.pageCount === 1) return applyAvgsOnly(coachingId, blobFromBytes(drop.data.bytes), onProgress)

  const prevAvgsPath = await getDocBucketAddress(coachingId, AVGS)
  const prevTbPath = await getDocBucketAddress(coachingId, TB_EMPTY)
  const prevDaten = await getDocData(coachingId, AVGS)

  const prepared = await prepareCombinedPages(drop.data, onProgress)
  if (!prepared.ok) return prepared
  const { originalPath: uploadPath, avgs: avgsBlob, tb: tbBlob, warnings } = prepared.data
  if (!tbBlob) return applyAvgsOnly(coachingId, avgsBlob, onProgress, { originalPath: uploadPath, warnings })

  const stored = await uploadAll(
    [
      { name: versionedFile('avgs', 'pdf'), blob: avgsBlob },
      { name: versionedFile('bestaetigung-empty', 'pdf'), blob: tbBlob },
    ],
  )
  const [avgsPath, tbPath] = stored ?? ['', '']
  if (!stored) {
    await cleanupAll([uploadPath])
    return technical('AVGS/Trägerbestätigung konnte nicht gespeichert werden.')
  }

  const written = await writeSplitPair({
    coachingId,
    avgsDaten: { combinedOriginal: uploadPath },
    avgsPath,
    avgsSize: avgsBlob.size,
    tbPath,
    tbSize: tbBlob.size,
    prevAvgsPath,
    restoreDaten: prevDaten,
    cleanupPaths: [uploadPath, avgsPath, tbPath],
  })
  if (!written.ok) return technical('AVGS/Trägerbestätigung konnte nicht gespeichert werden.')
  await retirePrevious(prevAvgsPath, avgsPath)
  await retirePrevious(prevTbPath, tbPath)
  await dropStaleOriginal(prevDaten, uploadPath)
  await invalidateFilledTb(coachingId, { dropFormDraft: true })

  onProgress?.('extracting')
  const extracted = await extractStoredAvgs(coachingId, avgsPath, uploadPath)
  if (!extracted.ok) return extracted
  await settleBestEffort(coachingId, 'avgsPruefen')
  return ok({ form: extracted.data, tbStored: true, warnings: warnings.length ? warnings : undefined })
}

async function applyAvgsOnly(
  coachingId: string,
  avgsBlob: Blob,
  onProgress?: (stage: CombinedUploadStage) => void,
  opts?: { originalPath: string; warnings: string[] },
): Promise<Result<{ form: AvgsForm; tbStored: boolean; warnings?: string[] }>> {
  const prevAvgsPath = await getDocBucketAddress(coachingId, AVGS)
  const prevDaten = await getDocData(coachingId, AVGS)

  onProgress?.('storing')
  let avgsPath = ''
  try {
    avgsPath = await uploadFile(avgsBlob, versionedFile('avgs', 'pdf'))
  } catch (e) {
    console.error('AVGS speichern fehlgeschlagen:', e)
    if (opts) await cleanupAll([opts.originalPath])
    return technical('AVGS konnte nicht gespeichert werden.')
  }
  try {
    await upsertDoc(coachingId, AVGS, {
      daten: opts ? { combinedOriginal: opts.originalPath } : {},
      bucketAddress: avgsPath,
      fileType: 'application/pdf',
      fileSize: avgsBlob.size,
    })
  } catch (e) {
    console.error('AVGS speichern fehlgeschlagen:', e)
    await cleanupAll(opts ? [avgsPath, opts.originalPath] : [avgsPath])
    return technical('AVGS konnte nicht gespeichert werden.')
  }
  await retirePrevious(prevAvgsPath, avgsPath)
  await dropStaleOriginal(prevDaten, opts?.originalPath)
  await invalidateFilledTb(coachingId)

  onProgress?.('extracting')
  const extracted = await extractStoredAvgs(coachingId, avgsPath, opts?.originalPath)
  if (!extracted.ok) return extracted
  await settleBestEffort(coachingId, 'avgsPruefen')
  return ok({ form: extracted.data, tbStored: false, warnings: opts?.warnings.length ? opts.warnings : undefined })
}

export async function reassignAvgsPage(coachingId: string, avgsPageIndex: number): Promise<Result<AvgsForm>> {
  const daten = await getDocData(coachingId, AVGS)
  const originalPath = daten?.combinedOriginal
  if (typeof originalPath !== 'string') {
    return technical(
      'Die ursprüngliche Datei ist nicht mehr vorhanden – bitte AVGS und Trägerbestätigung erneut hochladen.',
    )
  }

  const originalBlob = await downloadFile(originalPath)
  if (!originalBlob) {
    return technical(
      'Die ursprüngliche Datei ist nicht mehr vorhanden – bitte AVGS und Trägerbestätigung erneut hochladen.',
    )
  }
  const bytes = new Uint8Array(await originalBlob.arrayBuffer())
  const assembled = await assembledFromBytes(bytes)
  const split = await splitAt(assembled, avgsPageIndex)
  if (!split.ok) return split

  const prevAvgsPath = await getDocBucketAddress(coachingId, AVGS)
  const prevTbPath = await getDocBucketAddress(coachingId, TB_EMPTY)

  const stored = await uploadAll(
    [
      { name: versionedFile('avgs', 'pdf'), blob: split.data.avgs },
      { name: versionedFile('bestaetigung-empty', 'pdf'), blob: split.data.tb },
    ],
  )
  const [avgsPath, tbPath] = stored ?? ['', '']
  if (!stored) return technical('AVGS/Trägerbestätigung konnte nicht gespeichert werden.')

  const written = await writeSplitPair({
    coachingId,
    avgsDaten: { combinedOriginal: originalPath },
    avgsPath,
    avgsSize: split.data.avgs.size,
    tbPath,
    tbSize: split.data.tb.size,
    prevAvgsPath,
    restoreDaten: daten,
    cleanupPaths: [avgsPath, tbPath],
  })
  if (!written.ok) return technical('AVGS/Trägerbestätigung konnte nicht gespeichert werden.')
  await retirePrevious(prevAvgsPath, avgsPath)
  await retirePrevious(prevTbPath, tbPath)
  await invalidateFilledTb(coachingId, { dropFormDraft: true })

  return extractStoredAvgs(coachingId, avgsPath, originalPath)
}

export type AvgsAutoMatchFailure =
  | { kind: 'no-match'; vorname: string | null; nachname: string | null }
  | { kind: 'ambiguous'; vorname: string; nachname: string }
  | { kind: 'not-ready'; vorname: string; nachname: string }
  | { kind: 'no-candidates' }
  | { kind: 'technical'; message: string }

export type AvgsAutoMatchResult =
  | { ok: true; coachingId: string; tbStored: boolean; form: AvgsForm; warnings?: string[] }
  | ({ ok: false } & AvgsAutoMatchFailure)

async function matchAgainstCandidates(stage: {
  candidates: AvgsCandidate[]
  stagedPaths: readonly string[]
  avgs: Blob
  onExtracting?: () => void
}): Promise<{ ok: true; matchedId: string; avgs: AvgsForm } | ({ ok: false } & AvgsAutoMatchFailure)> {
  const { candidates, stagedPaths } = stage
  const cleanupStaged = () => cleanupAll(stagedPaths)

  stage.onExtracting?.()
  let extracted: Result<AvgsForm>
  try {
    extracted = await extractAvgs(stage.avgs)
  } catch (e) {
    await cleanupStaged()
    throw e
  }
  if (!extracted.ok) {
    await cleanupStaged()
    return {
      ok: false,
      kind: 'technical',
      message: extracted.kind === 'technical' ? extracted.message : 'AVGS-Erkennung fehlgeschlagen.',
    }
  }
  const avgs = extracted.data
  const person = { vorname: avgs.Teilnehmer?.Vorname, nachname: avgs.Teilnehmer?.Nachname }
  const match = matchTeilnehmende(person, candidates)

  if (match.kind === 'none') {
    await cleanupStaged()
    return { ok: false, kind: 'no-match', vorname: person.vorname || null, nachname: person.nachname || null }
  }
  if (match.kind === 'ambiguous') {
    await cleanupStaged()
    return { ok: false, kind: 'ambiguous', vorname: person.vorname || '', nachname: person.nachname || '' }
  }

  const matchedCandidate = match.candidate as AvgsCandidate
  if (!matchedCandidate.ready) {
    await cleanupStaged()
    return {
      ok: false,
      kind: 'not-ready',
      vorname: matchedCandidate.vorname,
      nachname: matchedCandidate.nachname,
    }
  }

  return { ok: true, matchedId: matchedCandidate.coachingId, avgs }
}

export async function applyCombinedUploadAutoMatch(
  files: readonly File[],
  onProgress?: (stage: CombinedUploadStage) => void,
): Promise<AvgsAutoMatchResult> {
  onProgress?.('splitting')
  const drop = await assemblePages(files)
  if (!drop.ok) {
    return {
      ok: false,
      kind: 'technical',
      message: drop.kind === 'technical' ? drop.message : 'Datei konnte nicht verarbeitet werden.',
    }
  }
  const candidates = await listAvgsCandidates()
  if (candidates.length === 0) return { ok: false, kind: 'no-candidates' }

  const prepared = drop.data.pageCount === 1 ? null : await prepareCombinedPages(drop.data, onProgress)
  if (prepared && !prepared.ok) {
    return { ok: false, kind: 'technical', message: prepared.kind === 'technical' ? prepared.message : 'Datei konnte nicht verarbeitet werden.' }
  }
  const parts = prepared?.data
  const avgsBlob = parts?.avgs ?? blobFromBytes(drop.data.bytes)
  const tbBlob = parts?.tb ?? null
  const avgsFileName = versionedFile('avgs', 'pdf')
  const tbFileName = tbBlob ? versionedFile('bestaetigung-empty', 'pdf') : null
  let stageAvgsPath: string
  let stageTbPath: string | null = null
  if (tbBlob && tbFileName && parts) {
    const stored = await uploadAll([
      { name: avgsFileName, blob: avgsBlob },
      { name: tbFileName, blob: tbBlob },
    ])
    if (!stored) {
      await cleanupAll([parts.originalPath])
      return { ok: false, kind: 'technical', message: 'AVGS/Trägerbestätigung konnte nicht gespeichert werden.' }
    }
    ;[stageAvgsPath, stageTbPath] = stored
  } else {
    onProgress?.('storing')
    try {
      stageAvgsPath = await uploadFile(avgsBlob, avgsFileName)
    } catch (e) {
      console.error('AVGS speichern fehlgeschlagen:', e)
      await cleanupAll(parts ? [parts.originalPath] : [])
      return { ok: false, kind: 'technical', message: 'AVGS konnte nicht gespeichert werden.' }
    }
  }
  const stagedPaths = [...(parts ? [parts.originalPath] : []), stageAvgsPath, ...(stageTbPath !== null ? [stageTbPath] : [])]
  const matched = await matchAgainstCandidates({
    candidates,
    stagedPaths,
    avgs: avgsBlob,
    onExtracting: () => onProgress?.('extracting'),
  })
  if (!matched.ok) return matched
  const { matchedId, avgs } = matched
  const prevAvgsPath = await getDocBucketAddress(matchedId, AVGS)
  const prevTbPath = tbBlob ? await getDocBucketAddress(matchedId, TB_EMPTY) : null
  const prevDaten = await getDocData(matchedId, AVGS)
  const daten = { ...(parts ? { combinedOriginal: parts.originalPath } : {}), extraction: avgs }
  try {
    if (tbBlob && stageTbPath !== null && parts) {
      const written = await writeSplitPair({
        coachingId: matchedId,
        avgsDaten: daten,
        avgsPath: stageAvgsPath,
        avgsSize: avgsBlob.size,
        tbPath: stageTbPath,
        tbSize: tbBlob.size,
        prevAvgsPath,
        restoreDaten: prevDaten,
        cleanupPaths: stagedPaths,
      })
      if (!written.ok) throw written.error
    } else {
      await upsertDoc(matchedId, AVGS, {
        daten,
        bucketAddress: stageAvgsPath,
        fileType: 'application/pdf',
        fileSize: avgsBlob.size,
      })
    }
  } catch (e) {
    if (!tbBlob) {
      console.error('AVGS speichern fehlgeschlagen:', e)
      await cleanupAll(stagedPaths)
    }
    return { ok: false, kind: 'technical', message: `${tbBlob ? 'AVGS/Trägerbestätigung' : 'AVGS'} konnte nicht gespeichert werden.` }
  }
  const finalAvgsPath = await relocateDocObject(stageAvgsPath, docPath(matchedId, avgsFileName))
  const finalTbPath = stageTbPath !== null && tbFileName
    ? await relocateDocObject(stageTbPath, docPath(matchedId, tbFileName))
    : stageTbPath
  await retirePrevious(prevAvgsPath, finalAvgsPath)
  if (finalTbPath !== null) await retirePrevious(prevTbPath, finalTbPath)
  await dropStaleOriginal(prevDaten, parts?.originalPath)
  if (tbBlob) await invalidateFilledTb(matchedId, { dropFormDraft: true })
  else await invalidateFilledTb(matchedId)
  await settleBestEffort(matchedId, 'avgsPruefen')
  return {
    ok: true,
    coachingId: matchedId,
    tbStored: !!tbBlob,
    form: avgs,
    ...(parts ? { warnings: parts.warnings.length ? parts.warnings : undefined } : {}),
  }
}

export async function loadAvgs(coachingId: string): Promise<Record<string, unknown> | null> {
  const d = await getDocData(coachingId, AVGS)
  return (d?.extraction as Record<string, unknown> | undefined) ?? null
}

export async function saveAvgsDraft(
  coachingId: string,
  values: Record<string, unknown>,
  identity: WriteIdentity = captureWriteIdentity(),
): Promise<void> {
  await patchDocData(coachingId, AVGS, { extraction: values }, undefined, identity)
}

async function submitsOriginal(coachingId: string, jobcenter: boolean): Promise<boolean> {
  const choice = await getTbEinreichung(coachingId)
  if (choice?.einreichung !== 'original' && jobcenter) return false
  const { position } = await getJourneyPosition(coachingId)
  if (position === null || position >= AUFGABE.vertragErstellen.position) return choice?.einreichung === 'original'
  if (choice?.einreichung !== 'original') await setTbEinreichung(coachingId, 'original')
  await discardBlankTb(coachingId)
  return true
}

export async function applyAvgsConfirm(
  coachingId: string,
  values: Record<string, unknown>,
): Promise<Result<void>> {
  try {
    const promotion = planAvgsPromotion(values)
    await promoteAvgs(coachingId, promotion)
    if (await submitsOriginal(coachingId, isJobcenter(promotion.agentur.behoerde))) {
      await advanceBestEffort(coachingId, 'tbAusfuellen')
      return ok(undefined)
    }
    const blankTbPath = await getDocBucketAddress(coachingId, TB_EMPTY)
    await advanceBestEffort(coachingId, blankTbPath ? 'tbAusfuellen' : 'tbHochladen')
    return ok(undefined)
  } catch (e) {
    console.error('AVGS übernehmen fehlgeschlagen:', e)
    return technical('AVGS-Daten konnten nicht übernommen werden.')
  }
}
